import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { type CellContext, type ColumnDef, type SortingState } from '@tanstack/react-table';
import { Badge, LoadingSpinner, Table, TextLink, Tooltip } from '@shared/components';
import { PermissionGuard } from '@/features/auth';
import { EyeIcon, EditIcon } from 'lucide-react';
import { DEALER_TYPE_LABELS, type Dealer, type DealerType } from '../types/dealers.types';
import { statusVariant } from '../utils/statusVariant';
import { canOwnChildren } from '../utils/dealerParentageRules';

interface Props {
  data: Dealer[];
  isLoading?: boolean;
  showGroupColumn?: boolean;
  sorting?: SortingState;
  onSortingChange?: (s: SortingState) => void;
}

export const DealerDataTable: React.FC<Props> = ({
  data,
  isLoading = false,
  showGroupColumn = true,
  sorting,
  onSortingChange,
}) => {
  const navigate = useNavigate();

  const columns = useMemo<ColumnDef<Dealer>[]>(() => [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: (info) => (
        <span className="font-semibold text-sm">{info.getValue() as string}</span>
      ),
    },
    {
      accessorKey: 'dealer_code',
      header: 'Code',
      cell: (info) => <span className="font-mono text-sm">{info.getValue() as string}</span>,
    },
    {
      accessorKey: 'status_key',
      id: 'status_id',
      header: 'Status',
      cell: (info) => {
        const key = info.getValue() as string | undefined;
        const label = (info.row.original.status as { label?: string } | undefined)?.label ?? key ?? '—';
        return (
          <Badge size="xs" variant={statusVariant(key)}>
            {label}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'city',
      header: 'City',
      cell: (info) => <span className="text-sm">{info.getValue() as string}</span>,
    },
    ...(showGroupColumn ? [{
      accessorKey: 'type',
      header: 'Type',
      enableSorting: false,
      cell: (info: CellContext<Dealer, unknown>) => {
        const effective: DealerType = (info.getValue() as DealerType | undefined) ?? 'dealer';
        const label = DEALER_TYPE_LABELS[effective];
        // Filled badge marks ownership-parent-capable types only. Derived from
        // the parentage matrix (dealer_group, oem_group) so the styling can
        // never imply an `oem` owns children when Spec A forbids it.
        const isParentType = canOwnChildren(effective);
        return <Badge size="xs" variant={isParentType ? 'info' : 'outline'}>{label}</Badge>;
      },
    }] : []),
    {
      accessorKey: 'child_dealers_count',
      header: 'Owned',
      // Defence-in-depth: backend list endpoints apply
      // `withChildDealersCount()` so this field is always populated. If a
      // future endpoint forgets the scope, render an em dash rather than
      // silently lying with "0". A real "0" still renders as "0".
      cell: (info) => {
        const value = info.getValue() as number | undefined;
        if (value === undefined || value === null) {
          return (
            <span className="block text-right text-sm tabular-nums font-medium text-mp-muted">
              —
            </span>
          );
        }
        return (
          <span className="block text-right text-sm tabular-nums font-medium">
            {value}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: (info) => (
        <div className="flex space-x-2">
          <Tooltip content="View dealer">
            <TextLink
              onClick={() => navigate(`/admin/dealers/${info.row.original.id}`)}
              color="primary"
              startIcon={<EyeIcon size={20} />}
            />
          </Tooltip>
          <PermissionGuard permission={{ resource: 'dealer', action: 'edit' }}>
            <Tooltip content="Edit dealer">
              <TextLink
                onClick={() => navigate(`/admin/dealers/${info.row.original.id}/edit`)}
                color="secondary"
                startIcon={<EditIcon size={18} />}
              />
            </Tooltip>
          </PermissionGuard>
        </div>
      ),
    },
  ], [showGroupColumn, navigate]);

  if (isLoading) {
    return <LoadingSpinner size="xl" className="py-12" message="Loading dealers..." />;
  }

  return (
    <Table<Dealer>
      data={data}
      columns={columns}
      sorting={sorting}
      onSortingChange={onSortingChange}
    />
  );
};
